import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import type { RuntimeEnvironment } from '../../graphql/config/runtimeConfig';
import type { CoreConfigurationQuery } from '../../graphql/queries/coreConfiguration.generated';
import type { ProvisionCoreApiTokenMutation } from '../../graphql/queries/provisionCoreApiToken.generated';
import type { ProvisionCoreWebhooksMutation } from '../../graphql/queries/provisionCoreWebhooks.generated';
import type { ReactivateCoreWebhooksMutation } from '../../graphql/queries/reactivateCoreWebhooks.generated';
import {
  CORE_CONFIGURATION_DOCUMENT,
  PROVISION_CORE_API_TOKEN_DOCUMENT,
  PROVISION_CORE_WEBHOOKS_DOCUMENT,
  REACTIVATE_CORE_WEBHOOKS_DOCUMENT,
} from './coreConfigurationDocuments';

export type CoreConfigurationFetchImpl = Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];

export type CoreConfigurationStatus = CoreConfigurationQuery['coreConfiguration'];
export type CoreExpectedWebhookStatus = CoreConfigurationStatus['webhooks'][number];

export type CoreConfigurationApi = {
  getStatus: (options?: { signal?: AbortSignal }) => Promise<CoreConfigurationStatus>;
  provisionApiToken: () => Promise<CoreConfigurationStatus>;
  provisionWebhooks: () => Promise<CoreConfigurationStatus>;
  reactivateWebhooks: () => Promise<CoreConfigurationStatus>;
};

type AuthenticatedClient = ReturnType<typeof createAuthenticatedGraphQLClient>;
type AuthenticatedClientFactory = (options: Parameters<typeof createAuthenticatedGraphQLClient>[0]) => AuthenticatedClient;

export type CreateCoreConfigurationApiOptions = {
  env?: RuntimeEnvironment;
  fetchImpl?: CoreConfigurationFetchImpl;
  authenticatedClientFactory?: AuthenticatedClientFactory;
};

const requireStatus = (status: CoreConfigurationStatus | null | undefined): CoreConfigurationStatus => {
  if (status == null) {
    throw new Error('Core configuration response was empty.');
  }

  return status;
};

// Field Ops derives brokerage and broker authorization from the bearer token.
// These operations take no variables; the browser never sends account id, token,
// webhook target, or Core webhook id.
export const createCoreConfigurationApi = ({
  env = resolveClientEnv(),
  fetchImpl,
  authenticatedClientFactory = createAuthenticatedGraphQLClient,
}: CreateCoreConfigurationApiOptions = {}): CoreConfigurationApi => {
  const client = authenticatedClientFactory({ env, fetchImpl });

  return {
    getStatus: async (options = {}) => {
      const result = await client.request<CoreConfigurationQuery>({
        document: CORE_CONFIGURATION_DOCUMENT,
        operationName: 'CoreConfiguration',
        signal: options.signal,
      });

      return requireStatus(result.coreConfiguration);
    },
    provisionApiToken: async () => {
      const result = await client.request<ProvisionCoreApiTokenMutation>({
        document: PROVISION_CORE_API_TOKEN_DOCUMENT,
        operationName: 'ProvisionCoreApiToken',
      });

      return requireStatus(result.provisionCoreApiToken);
    },
    provisionWebhooks: async () => {
      const result = await client.request<ProvisionCoreWebhooksMutation>({
        document: PROVISION_CORE_WEBHOOKS_DOCUMENT,
        operationName: 'ProvisionCoreWebhooks',
      });

      return requireStatus(result.provisionCoreWebhooks);
    },
    reactivateWebhooks: async () => {
      const result = await client.request<ReactivateCoreWebhooksMutation>({
        document: REACTIVATE_CORE_WEBHOOKS_DOCUMENT,
        operationName: 'ReactivateCoreWebhooks',
      });

      return requireStatus(result.reactivateCoreWebhooks);
    },
  };
};
